import { useState } from "react";
import { Share2, Copy, Check, ExternalLink, X, ShieldCheck } from "lucide-react";

const PUBLIC_VERIFY_BASE = "https://app.autheraapp.com";

function withHexPrefix(h) {
  if (!h) return null;
  const s = String(h).trim();
  return s.toLowerCase().startsWith("0x") ? s : `0x${s}`;
}

export function buildShareUrl(txHash) {
  const tx = withHexPrefix(txHash);
  if (!tx) return null;
  return `${PUBLIC_VERIFY_BASE}/verify/${tx}`;
}

export default function ShareCertificateDialog({ open, txHash, itemName, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const url = buildShareUrl(txHash);
  if (!url) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Best effort: do nothing on copy failure (browser denied perm).
    }
  };

  return (
    <div
      data-testid="share-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-[2px] px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-[0_24px_64px_rgba(0,0,0,0.18)] overflow-hidden"
      >
        <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />
        <div className="px-6 pt-5 pb-2 flex items-start justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#0055FF]">
              <Share2 size={12} />
              Share
            </div>
            <h2 className="font-display font-bold text-gray-900 text-xl mt-1">
              Share Digital Certificate
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-dialog-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 pb-2 text-sm text-gray-600 leading-relaxed">
          Share a secure verification link for this asset.
          {itemName ? (
            <span className="block mt-1 text-gray-900 font-medium">{itemName}</span>
          ) : null}
        </div>

        <div className="px-6 mt-4">
          <div
            data-testid="share-url"
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 font-mono text-xs text-gray-800 break-all"
          >
            {url}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-500">
            <ShieldCheck size={11} className="text-[#0055FF]" />
            Privacy-safe — no customer or wallet data is exposed.
          </div>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-2 border-t border-gray-100 mt-5 bg-gray-50/60">
          <button
            type="button"
            onClick={onCopy}
            data-testid="share-copy-link"
            className="inline-flex items-center justify-center gap-1.5 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-3 py-2.5 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy Link"}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="share-open-verify"
            className="inline-flex items-center justify-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-3 py-2.5 rounded-md hover:bg-[#0044CC] transition"
          >
            <ExternalLink size={14} />
            Open Verify Page
          </a>
        </div>
      </div>
    </div>
  );
}
